/**
 * Date Formatting Utilities for Travel Applications
 * 
 * CRITICAL: All travel dates/times are stored and displayed in LOCAL TIME
 * of the destination. We NEVER convert based on user's timezone because:
 * - Flight times are always in departure/arrival airport's local time
 * - Hotel check-in/out times are in the hotel's local time
 * - Activity times are in the destination's local time
 * 
 * This module provides timezone-safe formatting that preserves local time.
 * 
 * DATE FORMAT PREFERENCE:
 * Users can choose between MM/DD/YYYY, DD/MM/YYYY, and YYYY-MM-DD formats.
 * Use formatDateWithPreference() when you want to respect user's preference.
 * Use formatDateSafe() for consistent display (always "Mar 15, 2025" style).
 */

// Month names for formatting
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Get user's date format preference from localStorage
 * Falls back to MM/DD/YYYY if not set
 * 
 * @returns {string} Date format preference ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY-MM-DD')
 */
export const getDateFormatPreference = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user?.settings?.date_format) {
        return user.settings.date_format;
      }
    }
    // Try userSettings cache
    const settingsStr = localStorage.getItem('userSettings');
    if (settingsStr) {
      const settings = JSON.parse(settingsStr);
      if (settings?.date_format) {
        return settings.date_format;
      }
    }
  } catch (e) {
    // Ignore errors
  }
  return 'MM/DD/YYYY';
};

/**
 * Get user's time format preference from localStorage. Mirror of
 * getDateFormatPreference — same lookup order (`user.settings.time_format`
 * first, then `userSettings.time_format`). Returns '12h' by default.
 *
 * @returns {string} Either '12h' or '24h'
 */
export const getTimeFormatPreference = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user?.settings?.time_format) {
        return user.settings.time_format;
      }
    }
    const settingsStr = localStorage.getItem('userSettings');
    if (settingsStr) {
      const settings = JSON.parse(settingsStr);
      if (settings?.time_format) {
        return settings.time_format;
      }
    }
  } catch (e) {
    // Ignore errors
  }
  return '12h';
};

/**
 * Resolve the use24Hour flag — explicit override wins, otherwise read pref.
 * Centralized so both `formatTime` and `formatDateTimeSafe` honor settings
 * automatically when callers don't pass a value.
 */
const resolveUse24Hour = (override) => {
  if (typeof override === 'boolean') return override;
  return getTimeFormatPreference() === '24h';
};

/**
 * Parse YYYY-MM-DD string to Date object WITHOUT timezone conversion
 * 
 * IMPORTANT: new Date("2025-03-15") interprets as UTC midnight, causing
 * a -1 day shift in US timezones. This function avoids that bug.
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date|null} - JavaScript Date object in local timezone, or null
 */
export const parseDateLocal = (dateString) => {
  if (!dateString) return null;
  
  // Handle both YYYY-MM-DD and dates with time component
  const datePart = dateString.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) return null;
  
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  
  return new Date(year, month - 1, day);
};

/**
 * Format a Date object to YYYY-MM-DD string
 * 
 * @param {Date} date - JavaScript Date object
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export const formatDateLocal = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Format date according to user's preference (MM/DD/YYYY, DD/MM/YYYY, or YYYY-MM-DD)
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} format - Override format (optional, uses preference if not provided)
 * @param {object} options - Formatting options
 * @param {boolean} options.useMonthName - Use month name instead of number (e.g., "Mar" instead of "03")
 * @param {boolean} options.shortMonth - Use short month names (default: true when useMonthName is true)
 * @returns {string} - Formatted date string
 */
export const formatDateWithPreference = (dateStr, format = null, options = {}) => {
  if (!dateStr) return '';
  
  const date = parseDateLocal(dateStr);
  if (!date) return dateStr;
  
  const dateFormat = format || getDateFormatPreference();
  const { useMonthName = false, shortMonth = true } = options;
  
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  const monthNum = String(month + 1).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  const monthName = shortMonth ? MONTHS_SHORT[month] : MONTHS_LONG[month];
  
  if (useMonthName) {
    // Format with month name based on preference order
    if (dateFormat === 'DD/MM/YYYY') {
      return `${day} ${monthName} ${year}`;
    } else {
      return `${monthName} ${day}, ${year}`;
    }
  }
  
  // Numeric format
  switch (dateFormat) {
    case 'DD/MM/YYYY':
      return `${dayStr}/${monthNum}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${monthNum}-${dayStr}`;
    case 'MM/DD/YYYY':
    default:
      return `${monthNum}/${dayStr}/${year}`;
  }
};

/**
 * Safely format a date string for display WITHOUT timezone conversion
 * Uses natural language format: "Mar 15, 2025" or "15 Mar 2025" based on preference
 * 
 * This is the PRIMARY function to use for displaying dates throughout the app.
 * It handles both date-only (YYYY-MM-DD) and datetime strings correctly.
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD or with time component
 * @param {object} options - Formatting options
 * @param {boolean} options.includeYear - Include year in output (default: true)
 * @param {boolean} options.shortMonth - Use short month names (default: true)
 * @param {string} options.fallback - Fallback value if date is invalid (default: '')
 * @param {boolean} options.respectPreference - Use DD/MM vs MM/DD order from user preference (default: true)
 * @returns {string} - Formatted date string (e.g., "Mar 15, 2025" or "15 Mar 2025")
 */
export const formatDateSafe = (dateStr, options = {}) => {
  if (!dateStr) return options.fallback || '';
  
  const {
    includeYear = true,
    shortMonth = true,
    fallback = '',
    respectPreference = true
  } = options;
  
  // Accept Date objects directly so callers don't have to convert.
  let date;
  if (dateStr instanceof Date) {
    date = isNaN(dateStr.getTime()) ? null : dateStr;
  } else {
    // Parse without timezone conversion
    date = parseDateLocal(dateStr);
  }
  if (!date) return fallback;
  
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const monthName = shortMonth ? MONTHS_SHORT[month] : MONTHS_LONG[month];
  
  // Check user's date format preference for day/month order
  const useDDMM = respectPreference && getDateFormatPreference() === 'DD/MM/YYYY';
  
  if (useDDMM) {
    // European style: "15 Mar 2025" or "15 Mar"
    return includeYear ? `${day} ${monthName} ${year}` : `${day} ${monthName}`;
  } else {
    // US style: "Mar 15, 2025" or "Mar 15"
    return includeYear ? `${monthName} ${day}, ${year}` : `${monthName} ${day}`;
  }
};

/**
 * Format date as short format without year (e.g., "Mar 15" or "15 Mar")
 * Commonly used in cards and compact displays
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted date without year
 */
export const formatDateShort = (dateStr) => {
  return formatDateSafe(dateStr, { includeYear: false });
};

/**
 * Format date with full month name (e.g., "March 15, 2025")
 * Used in headers and detailed views
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted date with full month
 */
export const formatDateLong = (dateStr) => {
  return formatDateSafe(dateStr, { shortMonth: false });
};

/**
 * Format a date range (e.g., "Mar 15 - Mar 22, 2025" or "15 Mar - 22 Mar 2025")
 * Smart formatting: omits duplicate month/year when appropriate
 * Respects user's DD/MM vs MM/DD preference
 * 
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {string} - Formatted date range
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return formatDateSafe(startDate || endDate);
  }
  
  const start = parseDateLocal(startDate);
  const end = parseDateLocal(endDate);
  
  if (!start || !end) return '';
  
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const useDDMM = getDateFormatPreference() === 'DD/MM/YYYY';
  
  const startMonth = MONTHS_SHORT[start.getMonth()];
  const endMonth = MONTHS_SHORT[end.getMonth()];
  
  if (sameMonth) {
    // Same month
    if (useDDMM) {
      // "15 - 22 Mar 2025"
      return `${start.getDate()} - ${end.getDate()} ${startMonth} ${end.getFullYear()}`;
    } else {
      // "Mar 15 - 22, 2025"
      return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${end.getFullYear()}`;
    }
  } else if (sameYear) {
    // Same year, different months
    if (useDDMM) {
      // "15 Mar - 22 Apr 2025"
      return `${start.getDate()} ${startMonth} - ${end.getDate()} ${endMonth} ${end.getFullYear()}`;
    } else {
      // "Mar 15 - Apr 22, 2025"
      return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
    }
  } else {
    // Different years
    return `${formatDateSafe(startDate)} - ${formatDateSafe(endDate)}`;
  }
};

/**
 * Parse time string (HH:MM or HH:MM:SS) and format for display
 * Time is displayed AS-IS since it represents local time at destination
 * 
 * Honors the user's `time_format` preference (12h vs 24h) by default. Pass
 * an explicit boolean to override (e.g., for log timestamps that always
 * want 24h regardless of user preference).
 * 
 * @param {string} timeStr - Time string in HH:MM or HH:MM:SS format
 * @param {boolean} [use24Hour] - Override; defaults to user preference
 * @returns {string} - Formatted time string (e.g., "2:30 PM" or "14:30")
 */
export const formatTime = (timeStr, use24Hour) => {
  if (!timeStr) return '';
  
  // Handle both HH:MM and HH:MM:SS formats
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr;
  
  const hours = parseInt(parts[0], 10);
  const minutes = parts[1].padStart(2, '0');
  
  if (isNaN(hours)) return timeStr;
  
  if (resolveUse24Hour(use24Hour)) {
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }
  
  // 12-hour format
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes} ${period}`;
};

/**
 * Format datetime for display (date + time)
 * Handles datetime strings stored as "YYYY-MM-DDTHH:MM" format
 * 
 * IMPORTANT: The time component is LOCAL TIME at the destination,
 * NOT the user's timezone. We extract and display it as-is.
 * 
 * @param {string} dateTimeStr - DateTime string with T separator
 * @param {object} options - Formatting options
 * @param {boolean} options.includeYear - Include year (default: true)
 * @param {boolean} options.use24Hour - Use 24-hour time (default: false)
 * @returns {string} - Formatted datetime (e.g., "Mar 15, 2025 at 2:30 PM")
 */
export const formatDateTimeSafe = (dateTimeStr, options = {}) => {
  if (!dateTimeStr) return '';
  
  const { includeYear = true, use24Hour } = options;
  
  // Split date and time components
  const [datePart, timePart] = dateTimeStr.split('T');
  
  const dateFormatted = formatDateSafe(datePart, { includeYear });
  
  if (!timePart) return dateFormatted;
  
  // Extract time without timezone suffix (Z or +00:00)
  const timeOnly = timePart.split(/[Z+-]/)[0];
  const timeFormatted = formatTime(timeOnly, use24Hour);
  
  return timeFormatted ? `${dateFormatted} at ${timeFormatted}` : dateFormatted;
};

/**
 * Format time from a datetime string
 * Extracts and formats just the time portion
 * 
 * @param {string} dateTimeStr - DateTime string with T separator
 * @param {boolean} [use24Hour] - Override; defaults to user preference
 * @returns {string} - Formatted time only
 */
export const formatTimeFromDateTime = (dateTimeStr, use24Hour) => {
  if (!dateTimeStr || !dateTimeStr.includes('T')) return '';
  
  const timePart = dateTimeStr.split('T')[1];
  if (!timePart) return '';
  
  // Remove timezone suffix if present
  const timeOnly = timePart.split(/[Z+-]/)[0];
  return formatTime(timeOnly, use24Hour);
};

/**
 * Get relative date description (e.g., "Today", "Tomorrow", "In 3 days")
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} - Relative description or formatted date
 */
export const getRelativeDate = (dateStr) => {
  if (!dateStr) return '';
  
  const date = parseDateLocal(dateStr);
  if (!date) return '';
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
  
  return formatDateSafe(dateStr);
};

/**
 * Calculate days between two dates
 * 
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format  
 * @returns {number} - Number of days between dates
 */
export const daysBetween = (startDate, endDate) => {
  const start = parseDateLocal(startDate);
  const end = parseDateLocal(endDate);
  
  if (!start || !end) return 0;
  
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Check if a date is in the past
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} - True if date is before today
 */
export const isDatePast = (dateStr) => {
  const date = parseDateLocal(dateStr);
  if (!date) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return date < today;
};

/**
 * Check if a date is today
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} - True if date is today
 */
export const isDateToday = (dateStr) => {
  const date = parseDateLocal(dateStr);
  if (!date) return false;
  
  const today = new Date();
  return date.getFullYear() === today.getFullYear() &&
         date.getMonth() === today.getMonth() &&
         date.getDate() === today.getDate();
};

/**
 * Format a date for input[type="date"] elements
 * Always returns YYYY-MM-DD format
 * 
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} - Date in YYYY-MM-DD format for input elements
 */
export const formatDateForInput = (dateInput) => {
  if (!dateInput) return '';
  
  if (dateInput instanceof Date) {
    return formatDateLocal(dateInput);
  }
  
  // Already in correct format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return dateInput;
  }
  
  // Parse and reformat
  const date = parseDateLocal(dateInput);
  return date ? formatDateLocal(date) : '';
};

// Legacy export for backward compatibility
export const formatTimeSafe = formatTimeFromDateTime;
